import { toast } from 'sonner';
import { UrlCategories } from './types/analysisTypes';

export const fetchSitemapUrls = async (domain: string) => {
  try {
    toast.info('Sitemap.xml taranıyor...');
    
    const sitemapUrls = [
      `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://${domain}/sitemap.xml`)}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://${domain}/sitemap_index.xml`)}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://${domain}/wp-sitemap.xml`)}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://${domain}/sitemaps.xml`)}`
    ];

    let response;
    let validSitemapUrl;

    for (const url of sitemapUrls) {
      try {
        console.log(`Trying to fetch sitemap from: ${url}`);
        const resp = await fetch(url);
        if (resp.ok) {
          response = resp;
          validSitemapUrl = url;
          console.log(`Found valid sitemap at: ${url}`);
          break;
        }
      } catch (err) {
        console.log(`Failed to fetch ${url}:`, err);
        continue;
      }
    }

    if (!response || !validSitemapUrl) {
      console.log('No sitemap found, trying to discover URLs directly');
      return await discoverUrlsDirectly(domain);
    }

    console.log('Found valid sitemap at:', validSitemapUrl);
    const text = await response.text();
    
    if (!text || text.trim() === '') {
      console.log('Sitemap content is empty, trying to discover URLs directly');
      return await discoverUrlsDirectly(domain);
    }
    
    console.log('Sitemap content length:', text.length);

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, "text/xml");
    
    // Check if parsing failed
    const parserError = xmlDoc.getElementsByTagName('parsererror');
    if (parserError.length > 0) {
      console.log('XML parsing error, trying to discover URLs directly');
      return await discoverUrlsDirectly(domain);
    }
    
    // Check for sitemap index
    const sitemapTags = xmlDoc.getElementsByTagName("sitemap");
    if (sitemapTags.length > 0) {
      toast.info('Sitemap indeksi bulundu, alt sitemaplar taranıyor...');
      const urlList = [];
      for (let i = 0; i < sitemapTags.length; i++) {
        const locTag = sitemapTags[i].getElementsByTagName("loc")[0];
        if (locTag?.textContent) {
          try {
            const subProxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(locTag.textContent)}`;
            console.log('Fetching sub-sitemap:', subProxyUrl);
            const subSitemapResponse = await fetch(subProxyUrl);
            if (!subSitemapResponse.ok) {
              console.log(`Sub-sitemap fetch failed with status: ${subSitemapResponse.status}`);
              continue;
            }
            
            const subSitemapText = await subSitemapResponse.text();
            if (!subSitemapText || subSitemapText.trim() === '') {
              console.log('Sub-sitemap content is empty, skipping');
              continue;
            }
            
            const subXmlDoc = parser.parseFromString(subSitemapText, "text/xml");
            
            // Check if parsing failed
            const subParserError = subXmlDoc.getElementsByTagName('parsererror');
            if (subParserError.length > 0) {
              console.log('Sub-sitemap XML parsing error, skipping');
              continue;
            }
            
            const urls = subXmlDoc.getElementsByTagName("url");
            for (let j = 0; j < urls.length; j++) {
              const loc = urls[j].getElementsByTagName("loc")[0];
              if (loc?.textContent) urlList.push(loc.textContent);
            }
          } catch (err) {
            console.error('Error fetching sub-sitemap:', err);
            continue;
          }
        }
      }
      
      if (urlList.length === 0) {
        console.log('No URLs found in sub-sitemaps, trying to discover URLs directly');
        return await discoverUrlsDirectly(domain);
      }
      
      return urlList;
    }

    // Regular sitemap
    const urls = xmlDoc.getElementsByTagName("url");
    const urlList = [];
    for (let i = 0; i < urls.length; i++) {
      const loc = urls[i].getElementsByTagName("loc")[0];
      if (loc?.textContent) {
        urlList.push(loc.textContent);
      }
    }
    
    if (urlList.length === 0) {
      console.log('No URLs found in sitemap, trying to discover URLs directly');
      return await discoverUrlsDirectly(domain);
    }
    
    console.log('Found URLs:', urlList.length);
    return urlList;
  } catch (error) {
    console.error('Sitemap fetch error:', error);
    toast.error('Sitemap.xml okuma hatası: ' + (error as Error).message);
    
    // Fallback to direct URL discovery
    console.log('Falling back to direct URL discovery');
    return await discoverUrlsDirectly(domain);
  }
};

// Directly discover URLs by crawling the homepage and following links
const discoverUrlsDirectly = async (domain: string) => {
  try {
    toast.info('Sitemap bulunamadı, sayfa bağlantıları direkt taranıyor...');
    console.log('Starting direct URL discovery for domain:', domain);
    
    // First, get the homepage
    const homeUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://${domain}`)}`;
    const homeResponse = await fetch(homeUrl);
    if (!homeResponse.ok) {
      throw new Error(`Ana sayfa alınamadı: ${homeResponse.status}`);
    }
    
    const homeHtml = await homeResponse.text();
    console.log('Homepage HTML length:', homeHtml.length);
    
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(homeHtml, "text/html");
    
    // Get all links on the homepage
    const links = htmlDoc.getElementsByTagName('a');
    console.log('Found links on homepage:', links.length);
    
    const urlSet = new Set<string>();
    // Add the homepage as the first URL
    urlSet.add(`https://${domain}`);
    
    // Process all links to extract internal URLs
    for (let i = 0; i < links.length; i++) {
      const href = links[i].getAttribute('href');
      if (!href) continue;
      
      try {
        // Try to create a valid URL
        let url = href;
        
        // Handle relative URLs
        if (href.startsWith('/')) {
          url = `https://${domain}${href}`;
        } else if (!href.startsWith('http')) {
          // Skip anchors, javascript:, mailto:, etc.
          if (href.startsWith('#') || href.includes('javascript:') || href.includes('mailto:') || href.includes('tel:')) {
            continue;
          }
          url = `https://${domain}/${href}`;
        }
        
        // Make sure it's from the same domain
        const urlObj = new URL(url);
        if (urlObj.hostname === domain || urlObj.hostname === `www.${domain}`) {
          urlSet.add(url);
        }
      } catch (e) {
        // Invalid URL, skip it
        console.log(`Invalid URL: ${href}`, e);
        continue;
      }
    }
    
    console.log('Total discovered URLs:', urlSet.size);
    
    // If we still don't have any URLs, try to create some common paths
    if (urlSet.size <= 1) {
      console.log('Not enough URLs discovered, adding common paths');
      const commonPaths = [
        '', '/', '/about', '/about-us', '/contact', '/contact-us', 
        '/blog', '/news', '/products', '/services', '/faq', '/privacy-policy',
        '/terms-of-service', '/sitemap'
      ];
      
      for (const path of commonPaths) {
        urlSet.add(`https://${domain}${path}`);
      }
    }
    
    const urlList = Array.from(urlSet);
    if (urlList.length === 0) {
      toast.error('Hiçbir URL bulunamadı');
      // Return at least the homepage
      return [`https://${domain}`];
    }
    
    toast.success(`Toplam ${urlList.length} URL bulundu`);
    return urlList;
  } catch (error) {
    console.error('Direct URL discovery error:', error);
    toast.error('URL keşif hatası: ' + (error as Error).message);
    // Return at least the homepage URL so we have something
    return [`https://${domain}`];
  }
};

export const categorizeUrls = (urls: string[]): UrlCategories => {
  toast.info('URL\'ler kategorilere ayrılıyor...');
  
  const categories: UrlCategories = {
    blog: [],
    products: [],
    services: [],
    about: [],
    technical: [],
    other: []
  };

  urls.forEach(url => {
    if (url.includes('/blog/') || url.includes('/blog')) categories.blog.push(url);
    else if (url.includes('/products/') || url.includes('/urunler/') || url.includes('/product/')) categories.products.push(url);
    else if (url.includes('/services/') || url.includes('/hizmetler/') || url.includes('/service/')) categories.services.push(url);
    else if (url.includes('/about/') || url.includes('/hakkimizda/') || url.includes('/about-us/') || url.includes('/kurumsal/')) categories.about.push(url);
    else if (url.match(/(privacy|terms|gdpr|sitemap|robots|gizlilik|kosullar|sartlar)/)) categories.technical.push(url);
    else categories.other.push(url);
  });

  return categories;
};
